/* eslint-disable max-classes-per-file */
import {
  BulkJobStatus,
  ProjectReportBulkJob,
} from 'organisms/CloudOpsReportAdmin/bulk/types';

const TERMINAL: BulkJobStatus[] = [
  'completed',
  'completed_with_errors',
  'failed',
];
const RETRY_DELAYS = [2000, 4000, 8000, 15000, 30000];
const KNOWN: BulkJobStatus[] = [
  'pending',
  'running',
  'completed',
  'completed_with_errors',
  'failed',
];

export class PollingCancelledError extends Error {
  constructor() {
    super('Bulk job polling was cancelled');
    Object.setPrototypeOf(this, PollingCancelledError.prototype);
  }
}

export class InvalidBulkJobStatusError extends Error {
  constructor(status: string) {
    super(`Unknown bulk job status: ${status}`);
    Object.setPrototypeOf(this, InvalidBulkJobStatusError.prototype);
  }
}

export const isTerminalBulkJob = (status: BulkJobStatus) =>
  TERMINAL.includes(status);

interface PollOptions {
  jobId: number;
  getJob: (id: number) => Promise<ProjectReportBulkJob>;
  wait: (milliseconds: number) => Promise<void>;
  isCancelled: () => boolean;
  onProgress: (job: ProjectReportBulkJob) => void;
}

export async function pollBulkJob({
  jobId,
  getJob,
  wait,
  isCancelled,
  onProgress,
}: PollOptions): Promise<ProjectReportBulkJob> {
  let retryIndex = 0;

  while (!isCancelled()) {
    try {
      // Polls must be sequential so backoff and progress reflect server order.
      // eslint-disable-next-line no-await-in-loop
      const job = await getJob(jobId);
      if (isCancelled()) throw new PollingCancelledError();
      if (!KNOWN.includes(job.status)) {
        throw new InvalidBulkJobStatusError(job.status);
      }
      onProgress(job);
      retryIndex = 0;
      if (isTerminalBulkJob(job.status)) return job;
      // eslint-disable-next-line no-await-in-loop
      await wait(2000);
    } catch (error) {
      if (error instanceof PollingCancelledError || isCancelled()) {
        throw new PollingCancelledError();
      }
      if (error instanceof InvalidBulkJobStatusError) throw error;
      const delay = RETRY_DELAYS[Math.min(retryIndex, RETRY_DELAYS.length - 1)];
      retryIndex += 1;
      // eslint-disable-next-line no-await-in-loop
      await wait(delay);
    }
  }

  throw new PollingCancelledError();
}
